import { keepPreviousData, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import React, { useCallback, useState } from 'react';
import { Autocomplete } from 'src/components/Autocomplete';
import { CircularProgress } from 'src/components/CircularProgress';
import { TextField } from 'src/components/TextField';
import { Coupon } from 'src/models/coupon/_types';
import { searchCoupons } from 'src/models/coupon/coupon';

interface CouponAutocompleteProps {
  value: Coupon | null;
  text: string;
  onChange: (coupon: Coupon | null) => void;
  couponTypes: ('coupon_purchase' | 'coupon_subscription')[];
  searchWithNoOptions?: boolean;
  loading?: boolean;
}

export const CouponAutocomplete: React.FC<CouponAutocompleteProps> = ({
  value,
  onChange,
  text = 'Select Coupon',
  couponTypes,
  searchWithNoOptions = false,
  loading = false,
}) => {
  const [searchValue, setSearchValue] = useState('');

  const {
    data: coupons,
    refetch: refetchCoupons,
    isFetching,
  } = useQuery({
    queryKey: ['coupons', 'search', searchValue],
    queryFn: () => searchCoupons({ search: searchValue, couponTypes }),
    enabled: false,
    placeholderData: keepPreviousData,
    staleTime: 5000,
  });

  const debouncedSearch = useCallback(
    (value: string) => {
      const search = debounce((searchValue: string) => {
        if (searchWithNoOptions || searchValue.length >= 2) {
          refetchCoupons();
        }
      }, 300);
      search(value);
      return () => search.cancel();
    },
    [refetchCoupons, searchWithNoOptions]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleFocus = useCallback(() => {
    if (searchWithNoOptions || searchValue.length >= 2) {
      refetchCoupons();
    }
  }, [refetchCoupons, searchWithNoOptions, searchValue]);

  if (loading) {
    return (
      <CircularProgress
        size="small"
        sx={{
          margin: '8px auto',
          display: 'block',
        }}
      />
    );
  }

  return (
    <Autocomplete
      options={coupons || []}
      getOptionLabel={(option) => option?.code || ''}
      renderOption={(props, option) => (
        <li {...props}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div>{option?.code}</div>
            {option?.description && (
              <div style={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                {option.description}
              </div>
            )}
          </div>
        </li>
      )}
      value={value}
      onChange={(_, newValue: Coupon) => {
        onChange(newValue);
        setSearchValue(newValue?.code || '');
      }}
      onInputChange={handleInputChange}
      loading={isFetching}
      filterOptions={(x) => x}
      noOptionsText={
        searchValue || searchWithNoOptions
          ? 'No coupons found'
          : 'Start typing to find coupons'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={text}
          onFocus={handleFocus}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? (
                  <CircularProgress color="inherit" size="small" />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
